'use client';

import { cn } from '@core/utils';
import { Portal } from '@radix-ui/react-portal';
import { Slot } from '@radix-ui/react-slot';
import { useEffect } from 'react';

import SvgDelete from '../../../__generated__/icon/SvgDelete';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock/useBodyScrollLock';
import type { HTMLTagProps } from '../../../types';
import { Spinner } from '../Spinner/Spinner';

export interface ModalProps extends HTMLTagProps<'article'> {
  blurredBackground?: boolean;
  header?: React.ReactNode;
  closeButton?: boolean;
  footer?: React.ReactNode;
  buttonFooter?: React.ReactNode;
  opened?: boolean;
  onClose?: () => void;
  asChild?: boolean;
  wrapperClassName?: string;
  headerClassName?: string;
  loading?: boolean;
  description?: React.ReactNode;
}

export const Modal = ({
  //* Modal props
  opened = false,
  onClose,
  asChild = false,
  header,
  closeButton,
  footer,
  buttonFooter,
  //* HTML article props
  className,
  wrapperClassName,
  headerClassName,
  children,
  loading,
  description,
  ...restArticleProps
}: ModalProps) => {
  useBodyScrollLock(opened);

  useEffect(() => {
    if (!opened || loading) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [opened, loading, onClose]);

  if (!opened) return null;

  const ModalContents = asChild ? Slot : 'section';

  return (
    <Portal
      className={cn(
        'fixed left-0 top-0 z-20 flex size-full items-center',
        'justify-center font-400 text-Gray-700 typo-body3 animate-in fade-in',
        wrapperClassName,
      )}
    >
      <div
        className="absolute z-[-1] size-full bg-Gray-black/70"
        role="button"
        tabIndex={0}
        aria-label="모달 닫기"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (loading) return;

          onClose?.();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (loading) return;
            onClose?.();
          }
        }}
      />
      <article
        className={cn(
          'relative mx-24 rounded-medium bg-Gray-white',
          'box-border flex flex-col px-24 py-32 lg:px-32',
          'max-h-[380px] min-w-280 lg:max-h-[90%] lg:min-w-[400px]',
          className,
        )}
      >
        {loading ? (
          <div className="absolute inset-0 z-10 size-full rounded-medium bg-Gray-25/80 flex-center">
            <Spinner />
          </div>
        ) : null}
        {header ? (
          <header
            className={cn(
              'flex items-center justify-center font-700 typo-h5 lg:justify-between',
              'text-Gray-900',
              headerClassName,
            )}
          >
            {header}
            {closeButton && !loading ? (
              <button
                onClick={onClose}
                type="button"
                className="mb-auto hidden size-30 shrink-0 items-center justify-center rounded-pill bg-Gray-300 lg:flex"
              >
                <SvgDelete
                  className="size-13 stroke-Gray-700 text-Gray-700"
                  strokeWidth={1}
                />
              </button>
            ) : null}
          </header>
        ) : null}
        {description ? (
          <p className="mt-8 text-center text-Gray-700 typo-body3 lg:mt-12 lg:text-start">
            {description}
          </p>
        ) : null}
        <ModalContents
          {...restArticleProps}
          className={cn(
            'flex flex-1 flex-col',
            'overflow-auto whitespace-pre-wrap break-keep text-center',
            className,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </ModalContents>
        {buttonFooter ? (
          <footer className="mt-24 flex w-full flex-col-reverse gap-8 lg:mt-32 lg:flex-row [&>*]:flex-1">
            {buttonFooter}
          </footer>
        ) : null}
        {footer ? <footer className="mt-24 lg:mt-32">{footer}</footer> : null}
      </article>
    </Portal>
  );
};
